import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { DonationsFeedActions } from './actions';
import {
  adaptDonation,
  ApiDonation,
  Donation,
} from '../../types/donations-feed/models';

export type DonationsFeedState = EntityState<Donation> & {
  loading: boolean;
  error: string | null;
  // estado local de mis likes para respetar toggle real
  myLikes: Record<string, boolean>;
};

const adapter = createEntityAdapter<Donation>({
  selectId: (d) => d.id,
  sortComparer: (a, b) => {
    // si no hay fecha, lo tratamos como string vacío
    const dateA = a.dateISO ?? '';
    const dateB = b.dateISO ?? '';
    return dateB.localeCompare(dateA); // más reciente primero
  },
});

const initialState: DonationsFeedState = adapter.getInitialState({
  loading: false,
  error: null,
  myLikes: {},
});

export const donationsFeedReducer = createReducer(
  initialState,
  on(DonationsFeedActions.loadRequested, (s) => ({
    ...s,
    loading: true,
    error: null,
  })),
  on(DonationsFeedActions.loadSucceeded, (s, { items }) =>
    adapter.setAll(items, { ...s, loading: false })
  ),
  on(DonationsFeedActions.loadFailed, (s, { error }) => ({
    ...s,
    loading: false,
    error,
  })),

  // Optimista: aplicamos delta a likes
  on(DonationsFeedActions.likeOptimistic, (s, { id, delta, liked }) => {
    const myLikes = { ...s.myLikes, [id]: liked };
    return adapter.updateOne(
      {
        id,
        changes: { likes: Math.max(0, (s.entities[id]?.likes ?? 0) + delta) },
      },
      { ...s, myLikes }
    );
  }),

  // Confirmado: fijamos el número de likes que devuelve el backend
  on(DonationsFeedActions.likeConfirmed, (s, { id, likes, liked }) => {
    const myLikes = { ...s.myLikes, [id]: liked };
    return adapter.updateOne({ id, changes: { likes } }, { ...s, myLikes });
  }),

  // Error: revertimos el delta aplicado
  on(DonationsFeedActions.likeReverted, (s, { id }) => {
    const liked = !!s.myLikes[id];
    const delta = liked ? 1 : -1;
    // revertir delta aplicado en optimistic
    return adapter.updateOne(
      {
        id,
        changes: { likes: Math.max(0, (s.entities[id]?.likes ?? 0) - delta) },
      },
      s
    );
  }),

  //actualizar basado en lo que nos diga signalr
  on(DonationsFeedActions.realtimeDonationReceived, (state, { donation }) => {
    // donation viene crudo (Cosmos/SignalR) con PascalCase
    const normalized = adaptDonation(donation as ApiDonation);
    return adapter.addOne(normalized, state);
  })
);

export const donationsFeedAdapter = adapter;
